import styled from "@emotion/styled";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { ContactApi, FindAllContactWithFilterIRoute } from "../../api/Contacts";
import { UpdateContactsFlagAtom } from "../../atoms/UpdateContactsAtom";
import { ILabel } from "../../types/LabelTypes";
import { useModal } from "../Modal/Modal";
import { CampaignTab } from "../NewCampaign/Table/CampaignContactsModal";
import StyledButton from "../StyledButton";
import LabelSelect from "./LabelSelects";

const Container = styled.div`
  width: calc(100% + 50px);
  height: 200px;

  margin-top: 25px;
  padding: 25px 50px 0 25px;

  border-top: 1px solid;
  border-color: inherit;

  @media screen and (max-width: 1024px) {
    width: 100%;
    height: 100%;

    margin-top: 0;
    padding: 25px 50px 25px 25px;

    border-top: none;
  }
`;

const LabelContainer = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  span {
    font-size: 16px;
    line-height: 26px;
    font-weight: 400;
    color: #333333;
  }
`;

const Title = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 600;
  color: #2f2f2f;
`;

const ModalFooter = styled.div`
  width: calc(100% + 50px);

  padding: 25px 25px 0 25px;

  border-top: 1px solid;
  border-color: inherit;

  display: flex;
  align-items: center;
  justify-content: end;
  grid-gap: 12px;

  @media screen and (max-width: 1024px) {
    width: 100%;

    padding: 25px;
  }
`;

const LabelModalContainer = styled.div`
  width: 400px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100%;
  }
`;

export enum BulkAddOrRemoveLabelFromPage {
  Contact = "CONTACT",
  Campaign = "CAMPAIGN",
}

enum LabelOperation {
  ADD,
  REMOVE,
}

interface IBulkAddOrRemoveLabel {
  setLoadingBlock: (value: boolean) => void;
  contacts?: string[];
  query?: FindAllContactWithFilterIRoute;
}

export default function BulkAddOrRemoveLabel(props: IBulkAddOrRemoveLabel) {
  const { setLoadingBlock } = props;

  const [labelsToAdd, setLabelsToAdd] = useState<ILabel[]>([]);
  const [labelsToRemove, setLabelsToRemove] = useState<ILabel[]>([]);

  const setUpdateContactFlag = useSetRecoilState(UpdateContactsFlagAtom);

  const modal = useModal();

  function handleAddOrRemoveLabel(label: ILabel, operation: LabelOperation) {
    let labels =
      operation === LabelOperation.ADD ? labelsToAdd : labelsToRemove;

    if (labels.find((l) => l._id === label._id)) {
      const newLabels = labels.filter((l) => l._id !== label._id);

      if (operation === LabelOperation.ADD) {
        setLabelsToAdd(newLabels);
      } else {
        setLabelsToRemove(newLabels);
      }
    } else {
      if (operation === LabelOperation.ADD) {
        setLabelsToAdd([...labels, label]);
      } else {
        setLabelsToRemove([...labels, label]);
      }
    }
  }

  async function addOrRemove() {
    setLoadingBlock(true);
    try {
      await ContactApi.addOrRemoveManyLabels({
        data: {
          addLabelIds: labelsToAdd.map((l) => l._id),
          removeLabelIds: labelsToRemove.map((l) => l._id),
          query: props.query,
          contacts: props.contacts,
        },
      });

      toast("Success", {
        type: "success",
      });
    } catch (err) {
      console.log("Error", err);

      toast("Unexpected Error", {
        type: "error",
      });
    } finally {
      setUpdateContactFlag(true);
      setLoadingBlock(false);
      modal.popModal();
    }
  }

  return (
    <LabelModalContainer>
      <Container>
        <Title>Apply Labels</Title>
        <LabelContainer style={{ marginBottom: "25px" }}>
          <span>Choose labels to apply</span>
          <LabelSelect
            noFilter
            activeLabels={labelsToAdd}
            selectLabelHandler={(label) =>
              handleAddOrRemoveLabel(label, LabelOperation.ADD)
            }
            labelsIdsToRemove={labelsToRemove.map((l) => l._id)}
          />
        </LabelContainer>

        <Title>Remove Labels</Title>
        <LabelContainer>
          <span>Choose labels to remove</span>
          <LabelSelect
            noFilter
            activeLabels={labelsToRemove}
            selectLabelHandler={(label) =>
              handleAddOrRemoveLabel(label, LabelOperation.REMOVE)
            }
            labelsIdsToRemove={labelsToAdd.map((l) => l._id)}
          />
        </LabelContainer>
      </Container>

      <ModalFooter>
        <StyledButton
          inverted
          style={{ width: "70px" }}
          onClick={modal.popModal}
        >
          Cancel
        </StyledButton>
        <StyledButton style={{ width: "70px" }} onClick={addOrRemove}>
          Save
        </StyledButton>
      </ModalFooter>
    </LabelModalContainer>
  );
}
